import {
    AllDaysOpeningHours,
    OffPeakTimes,
    PinoDetails,
    PinoProductDetails,
    TggMembershipType,
    TggPinoProductTitle,
} from '@tgg/common-types';
import {
    dateStringTo12HourTimeForSmallExpander,
    monthlyProductTitleMap,
} from '@tgg/util';

import { SmallExpander } from '../../components/SmallExpander';
import { palette } from '../../theme';
import { Icon } from '../Icon';

import { Styled24Div } from './pino-table.styled';

const dayPassBenefitsMbtV3 = {
    includedBenefits: {
        'Free classes': {
            display: 'Free classes',
            rowSpan: 1,
        },
        '24/7 Gym Access': {
            display: '24/7 Gym Access',
            rowSpan: 1,
        },
        'Get Access to 2 gyms': {
            display: 'Get Access to 2 gyms',
            rowSpan: 1,
        },
    },
    title: TggPinoProductTitle.DayPass,
    position: 0,
};

const getDayPassBenefits = (isMBTOptimizationV2: boolean): PinoDetails => ({
    includedBenefits: {
        'Unlimited training': {
            display: isMBTOptimizationV2 ? (
                '24/7'
            ) : (
                <Icon
                    name="tick"
                    color={palette.common.blue}
                    size={18}
                    accessibleTitle="Day pass product has unlimited training access"
                />
            ),
            rowSpan: 1,
        },
        'Free classes': {
            display: (
                <Icon
                    name="tick"
                    color={palette.common.blue}
                    size={18}
                    accessibleTitle="Day pass product has free classes"
                />
            ),
            rowSpan: 1,
        },
        'Single-gym access': {
            display: (
                <Icon
                    name="tick"
                    color={palette.common.blue}
                    size={18}
                    accessibleTitle="student product has single-gym access"
                />
            ),
            rowSpan: 1,
        },
    },
    title: TggPinoProductTitle.DayPass,
    position: 0,
});

export function formatGymHours(daysData: any): JSX.Element[] {
    if (!Array.isArray(daysData) || daysData.length === 0 || !daysData[0]) {
        return [];
    }

    const DAYS = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ];

    const LABELS: Record<string, string> = {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun',
    };

    const flatten = daysData[0] || {};
    const scheduleMap: Record<string, string[]> = {};
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const normalizeTimeString = (time: string): string => {
        if (time.includes('T')) return time;

        return `1900-01-01T${time}`;
    };

    for (const day of DAYS) {
        const dayPeriods = flatten[day]
            ? Array.isArray(flatten[day])
                ? flatten[day]
                : [flatten[day]]
            : [];
        const rangeKey = JSON.stringify(
            dayPeriods.map((p: any) => ({
                open: new Date(
                    normalizeTimeString(p.openingTime || p.OpeningTime),
                ).toISOString(),
                close: new Date(
                    normalizeTimeString(p.closingTime || p.ClosingTime),
                ).toISOString(),
            })),
        );

        if (!scheduleMap[rangeKey]) scheduleMap[rangeKey] = [];

        scheduleMap[rangeKey].push(day);
    }

    const groupDayRanges = (days: string[]): string[] => {
        const groups: string[] = [];
        for (let index = 0; index < days.length; ) {
            const start = index;
            while (
                index + 1 < days.length &&
                DAYS.indexOf(days[index + 1]) === DAYS.indexOf(days[index]) + 1
            ) {
                index++;
            }
            const rangeGroup = days.slice(start, index + 1);
            if (rangeGroup.length === 1) {
                groups.push(LABELS[rangeGroup[0]]);
            } else {
                groups.push(
                    `${LABELS[rangeGroup[0]]}–${LABELS[rangeGroup.at(-1)!]}`,
                );
            }
            index++;
        }
        return groups;
    };

    return Object.entries(scheduleMap)
        .map(([key, groupedDays], index) => {
            const periods = JSON.parse(key);
            if (periods.length === 0) return null;
            const is24hr =
                periods.length === 1 &&
                (() => {
                    const open = new Date(periods[0].open);
                    const close = new Date(periods[0].close);
                    const durationMs = close.getTime() - open.getTime();
                    const durationHours = durationMs / (1000 * 60 * 60);
                    return Math.abs(durationHours - 24) < 0.01;
                })();

            const dayLabel = groupDayRanges(groupedDays).join(', ');

            return (
                <p key={index}>
                    <span>{dayLabel}:</span>{' '}
                    {is24hr ? (
                        <span>24hr access</span>
                    ) : (
                        periods.map((p: any, index_: number) => {
                            const open = dateStringTo12HourTimeForSmallExpander(
                                new Date(p.open),
                            );
                            const close =
                                dateStringTo12HourTimeForSmallExpander(
                                    new Date(p.close),
                                );
                            const isLast = index_ === periods.length - 1;

                            return (
                                <span key={index_}>
                                    {`${open}–${close}${!isLast ? ', ' : ''}`}
                                </span>
                            );
                        })
                    )}
                </p>
            );
        })
        .filter((element): element is JSX.Element => element !== null);
}

const normalizeOpeningHours = (openingHours: AllDaysOpeningHours) => {
    if (!openingHours) return {};

    const output: Record<
        string,
        { openingTime: string; closingTime: string }[]
    > = {};

    for (const [day, times] of Object.entries(openingHours)) {
        const key = day.toLowerCase();
        const timesArray = Array.isArray(times) ? times : [times];

        output[key] = timesArray.map(time => ({
            openingTime: time.OpeningTime.replace(' ', 'T'),
            closingTime: time.ClosingTime.replace(' ', 'T'),
        }));
    }

    return output;
};

export const getPinoProductBenefits = (
    isGuestPassEnabled: boolean,
    isMBTOptimization = false,
    isMBTOptimizationV1 = false,
    isMBTOptimizationV2 = false,
    isMBTOptimizationV3 = false,
    offPeakTimes?: OffPeakTimes[],
    openingHours?: AllDaysOpeningHours,
    isOpen24Hours?: boolean,
): PinoProductDetails => {
    const fitnessProgressTracking = `${isMBTOptimization ? 'Fitness progress tracking' : 'Fitness & body composition tracking (where available)'}`;
    const freeFiitTitle = `${isMBTOptimization ? 'Free Fiit plans & workouts' : 'Free Fiit workouts & plans worth £20 a month'}`;
    const freeFreezeTitle = `${isMBTOptimization ? 'Free Freeze' : 'Freeze your monthly membership once for free'}`;
    const multiGymAccess = `${isMBTOptimizationV1 || isMBTOptimizationV2 ? 'Multi-gym access' : 'Access all gyms'}`;
    const cellHyphen = isMBTOptimization ? '—' : '-';
    const studentDetails: PinoDetails = {
        includedBenefits: {
            'Free classes': {
                display: (
                    <div aria-label="student product has 24/7 access to Free classes and Gym Access">
                        <div>24/7</div>
                        <div>access</div>
                    </div>
                ),
                rowSpan: 2,
            },
            'Gym Access': {
                display: '',
                rowSpan: 0,
            },
            'Choose 2 gyms': {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="student product has Multi-gym access"
                    />
                ),
                rowSpan: 1,
            },
            'Discounts & offers': {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="standard product has Discounts & offers"
                    />
                ),
                rowSpan: 1,
            },
            [fitnessProgressTracking]: {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="student product has Fitness & body composition tracking (where available)"
                    />
                ),
                rowSpan: 1,
            },
        },
        title: TggPinoProductTitle.Student,
        position: 0,
    };

    const studentDetailsMBTOptimizationV2: PinoDetails = {
        includedBenefits: {
            'Gym Access': {
                display: !isOpen24Hours ? (
                    <SmallExpander title="All Hours Access" tab="student">
                        {formatGymHours([
                            openingHours && normalizeOpeningHours(openingHours),
                        ])}
                    </SmallExpander>
                ) : (
                    <Styled24Div>24/7</Styled24Div>
                ),
                rowSpan: 1,
            },
            'Free classes': {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="student product has access to Free Classes"
                    />
                ),
                rowSpan: 1,
            },
            'Choose 2 gyms': {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="student product has Multi-gym access"
                    />
                ),
                rowSpan: 1,
            },
            [fitnessProgressTracking]: {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="student product has Fitness & body composition tracking (where available)"
                    />
                ),
                rowSpan: 1,
            },
            'Discounts & offers': {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle="standard product has Discounts & offers"
                    />
                ),
                rowSpan: 1,
            },
        },
        title: TggPinoProductTitle.Student,
        position: 0,
    };

    const studentBenefits = isMBTOptimizationV2
        ? studentDetailsMBTOptimizationV2
        : studentDetails;

    const getGuestPassBenefit = (membershipType: TggMembershipType) => {
        const isUltimateMembership =
            membershipType === TggMembershipType.UltimateMonthly;
        const isStandartMonthlyMembership =
            membershipType === TggMembershipType.StandardMonthly;
        const isOffPeakMembership =
            membershipType === TggMembershipType.OffPeakMonthly;
        const membershipLabelMap = {
            [TggMembershipType.UltimateMonthly]: 'ultimate',
            [TggMembershipType.StandardMonthly]: 'standard',
            [TggMembershipType.OffPeakMonthly]: 'off-peak',
        };
        const benefitText = isGuestPassEnabled
            ? `${isMBTOptimization ? 'Guest Passes' : '4 Guest Passes per month'}`
            : 'Bring a friend 4 times per month';

        const benefitContent =
            (isStandartMonthlyMembership || isOffPeakMembership) && cellHyphen;

        const ariaLabel = `${
            membershipLabelMap[
                membershipType as keyof typeof membershipLabelMap
            ]
        } product ${
            isUltimateMembership ? 'has' : 'does not have'
        } ${benefitText}`;

        return {
            [benefitText]: {
                display: (
                    <span aria-label={ariaLabel}>
                        {isUltimateMembership ? (
                            <Icon
                                name="tick"
                                color={palette.common.blue}
                                size={18}
                                accessibleTitle={ariaLabel}
                            />
                        ) : (
                            benefitContent
                        )}
                    </span>
                ),
                rowSpan: 1,
            },
        };
    };

    const getGymAccessAndFreeClassesCellContent = (
        membership: string,
        isOffPeak: boolean,
        isStandard: boolean,
    ) => {
        const normalizedHours =
            openingHours && normalizeOpeningHours(openingHours);
        const standardCellContent =
            isStandard && !isOpen24Hours ? (
                <SmallExpander title="All Hours" tab="memberships">
                    {openingHours && formatGymHours([normalizedHours])}
                </SmallExpander>
            ) : (
                <Styled24Div>24/7</Styled24Div>
            );

        const MBTOptimizationCellsContent = {
            'Gym Access': {
                display: (
                    <div
                        aria-label={
                            isOffPeak
                                ? 'off-peak product has access to Free classes and Gym Access during off-peak hours'
                                : `${membership} product has 24/7 access to Gym Access`
                        }
                    >
                        {isOffPeak ? (
                            <SmallExpander title="Off-Peak">
                                {offPeakTimes && formatGymHours(offPeakTimes)}
                            </SmallExpander>
                        ) : (
                            standardCellContent
                        )}
                    </div>
                ),
                rowSpan: 1,
            },
            'Free classes': {
                display: (
                    <Icon
                        name="tick"
                        color={palette.common.blue}
                        size={18}
                        accessibleTitle={`${membership} product has Free classes`}
                    />
                ),
                rowSpan: 1,
            },
        };
        const cellsContent = {
            'Free classes': {
                display: isOffPeak ? (
                    <span aria-label="off-peak product has access to Free classes and Gym Access during off-peak hours">
                        <div>during</div>
                        <div>off-peak</div>
                    </span>
                ) : (
                    <div
                        aria-label={`${membership} product has 24/7 access to Free classes and Gym Access`}
                    >
                        <div>24/7</div>
                        <div>access</div>
                    </div>
                ),
                rowSpan: 2,
                linkHref: isOffPeak && '#gym_times_location_section',
            },
            'Gym Access': {
                display: '',
                rowSpan: 0,
            },
        };

        return isMBTOptimizationV2 || isMBTOptimizationV3
            ? MBTOptimizationCellsContent
            : cellsContent;
    };

    return {
        [TggMembershipType.UltimateMonthly]: {
            includedBenefits: {
                ...getGymAccessAndFreeClassesCellContent(
                    'ultimate',
                    false,
                    false,
                ),
                [multiGymAccess]: {
                    display: (
                        <Icon
                            name="tick"
                            color={palette.common.blue}
                            size={18}
                            accessibleTitle="ultimate product has Multi-gym access"
                        />
                    ),
                    rowSpan: 1,
                },
                [freeFreezeTitle]: {
                    display: (
                        <Icon
                            name="tick"
                            color={palette.common.blue}
                            size={18}
                            accessibleTitle="ultimate product has Freeze your monthly membership once for free"
                        />
                    ),
                    rowSpan: 1,
                },
                ...getGuestPassBenefit(TggMembershipType.UltimateMonthly),
                [freeFiitTitle]: {
                    display: (
                        <Icon
                            name="tick"
                            color={palette.common.blue}
                            size={18}
                            accessibleTitle="ultimate product has Free Fiit workouts & plans worth £20 a month"
                        />
                    ),
                    rowSpan: 1,
                },
                'Additional benefits': {
                    display: (
                        <Icon
                            name="plus"
                            color={palette.common.blue}
                            size={18}
                            accessibleTitle="additional benefits button expander"
                        />
                    ),
                    rowSpan: 1,
                },
                [fitnessProgressTracking]: {
                    display: (
                        <Icon
                            name="tick"
                            color={palette.common.blue}
                            size={18}
                            accessibleTitle="ultimate product has Fitness & body composition tracking (where available)"
                        />
                    ),
                    rowSpan: 1,
                },
            },
            title: monthlyProductTitleMap.UltimateMonthly,
            position: 0,
        },
        [TggMembershipType.StandardMonthly]: {
            includedBenefits: {
                ...getGymAccessAndFreeClassesCellContent(
                    'standard',
                    false,
                    true,
                ),
                [multiGymAccess]: {
                    display: (
                        <span aria-label="standard product does not have Multi-gym access">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                [freeFreezeTitle]: {
                    display: (
                        <span aria-label="standard product does not have Freeze your monthly membership once for free">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                ...getGuestPassBenefit(TggMembershipType.StandardMonthly),
                [freeFiitTitle]: {
                    display: (
                        <span aria-label="standard product does not have Free Fiit workouts & plans worth £20 a month">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Additional benefits': {
                    display: (
                        <span aria-label="standard product does not have any additional benefits">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                [fitnessProgressTracking]: {
                    display: (
                        <span aria-label="standard product does not have Fitness & body composition tracking (where available)">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
            },
            title: monthlyProductTitleMap.StandardMonthly,
            position: 1,
        },
        [TggMembershipType.OffPeakMonthly]: {
            includedBenefits: {
                ...getGymAccessAndFreeClassesCellContent(
                    'off-peak',
                    true,
                    false,
                ),
                [multiGymAccess]: {
                    display: (
                        <span aria-label="off-peak product does not have Multi-gym access">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                [freeFreezeTitle]: {
                    display: (
                        <span aria-label="off-peak product does not have Freeze your monthly membership once for free">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                ...getGuestPassBenefit(TggMembershipType.OffPeakMonthly),
                [freeFiitTitle]: {
                    display: (
                        <span aria-label="off-peak product does not have Free Fiit workouts & plans worth £20 a month">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Additional benefits': {
                    display: (
                        <span aria-label="off-peak product does not have any additional benefits">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
                [fitnessProgressTracking]: {
                    display: (
                        <span aria-label="off-peak product does not have Fitness & body composition tracking (where available)">
                            {cellHyphen}
                        </span>
                    ),
                    rowSpan: 1,
                },
            },
            title: monthlyProductTitleMap.OffPeakMonthly,
            position: 2,
        },
        [TggMembershipType.StudentFitquestGymAccess]: studentBenefits,
        [TggMembershipType.Student3]: studentBenefits,
        [TggMembershipType.Student6]: studentBenefits,
        [TggMembershipType.Student9]: studentBenefits,
        [TggMembershipType.Student12]: studentBenefits,
        [TggMembershipType.DayPass]: getDayPassBenefits(isMBTOptimizationV2),
        [TggMembershipType.DayPass3]: getDayPassBenefits(isMBTOptimizationV2),
        [TggMembershipType.DayPass5]: getDayPassBenefits(isMBTOptimizationV2),
    } as unknown as PinoProductDetails;
};

export const getPinoProductBenefitsMBTV3 = (
    offPeakTimes?: OffPeakTimes[],
    openingHours?: AllDaysOpeningHours,
    isOpen24Hours?: boolean,
): PinoProductDetails => {
    const studentDetails: PinoDetails = {
        includedBenefits: {
            'Gym Access': {
                display: !isOpen24Hours ? (
                    <SmallExpander title="All Hours Access" tab="student">
                        {formatGymHours([
                            openingHours && normalizeOpeningHours(openingHours),
                        ])}
                    </SmallExpander>
                ) : (
                    <Styled24Div>24/7 Gym Access</Styled24Div>
                ),
                rowSpan: 1,
            },
            'Free classes': {
                display: (
                    <div aria-label="student product has 24/7 Gym Access">
                        Free Classes
                    </div>
                ),
                rowSpan: 1,
            },
            'Choose 2 gyms': {
                display: (
                    <div aria-label="student product has access to 2 gyms">
                        Get Access to 2 gyms
                    </div>
                ),
                rowSpan: 1,
            },
            'Fitness progress tracking': {
                display: (
                    <div aria-label="student product has access to fitness progress tracking">
                        Fitness progress tracking
                    </div>
                ),
                rowSpan: 1,
            },
            'Discounts & offers': {
                display: (
                    <div aria-label="standard product has discounts & offers">
                        Discounts & offers
                    </div>
                ),
                rowSpan: 1,
            },
        },
        title: TggPinoProductTitle.Student,
        position: 0,
    };

    const getGuestPassBenefitV3 = (membershipType: TggMembershipType) => {
        const isUltimateMonthlyMembership =
            membershipType === TggMembershipType.UltimateMonthly;
        const membershipLabelMap = {
            [TggMembershipType.UltimateMonthly]: 'ultimate',
            [TggMembershipType.StandardMonthly]: 'standard',
            [TggMembershipType.OffPeakMonthly]: 'off-peak',
        };

        const ariaLabel = `${
            membershipLabelMap[
                membershipType as keyof typeof membershipLabelMap
            ]
        } product ${
            isUltimateMonthlyMembership ? 'has' : 'does not have'
        } ${'Guest Passes'}`;

        return {
            'Guest Passes': {
                display: (
                    <span>
                        {isUltimateMonthlyMembership ? (
                            <div aria-label={ariaLabel}>Guest Passes</div>
                        ) : (
                            <div aria-label={ariaLabel}>—</div>
                        )}
                    </span>
                ),
                rowSpan: 1,
            },
        };
    };

    const getGymAccessAndFreeClassesCellContentV3 = (
        isOffPeak?: boolean,
        isStandard?: boolean,
    ) => {
        const normalizedHoursV3 =
            openingHours && normalizeOpeningHours(openingHours);
        const standardCellContentV3 =
            isStandard && !isOpen24Hours ? (
                <SmallExpander title="All Hours Access" tab="memberships">
                    {openingHours && formatGymHours([normalizedHoursV3])}
                </SmallExpander>
            ) : (
                <Styled24Div>24/7 Access</Styled24Div>
            );

        return {
            'Gym Access': {
                display: (
                    <div
                        aria-label={
                            isOffPeak
                                ? 'off-peak product has access to Free classes and Gym Access during off-peak hours'
                                : 'ultimate and standard product has 24/7 access to Gym Access'
                        }
                    >
                        {isOffPeak ? (
                            <SmallExpander title="Off-Peak Hours">
                                {formatGymHours(offPeakTimes)}
                            </SmallExpander>
                        ) : (
                            standardCellContentV3
                        )}
                    </div>
                ),
                rowSpan: 1,
            },
            'Free classes': {
                display: (
                    <div
                        aria-label={
                            isOffPeak
                                ? 'off-peak product has access to 60 classes'
                                : 'ultimate and standard products have access to 120 classes'
                        }
                    >
                        <div>Free Classes</div>
                    </div>
                ),
                rowSpan: 1,
            },
        };
    };

    return {
        [TggMembershipType.UltimateMonthly]: {
            includedBenefits: {
                ...getGymAccessAndFreeClassesCellContentV3(false, false),
                'Access all gyms': {
                    display: (
                        <div aria-label="ultimate products has multi-gym access">
                            Multi-gym access
                        </div>
                    ),
                    rowSpan: 1,
                },
                'Free Freeze': {
                    display: (
                        <div aria-label="ultimate product has Freeze your monthly membership once for free">
                            Free Freeze
                        </div>
                    ),
                    rowSpan: 1,
                },
                ...getGuestPassBenefitV3(TggMembershipType.UltimateMonthly),
                'Free Fiit plans & workouts': {
                    display: (
                        <div aria-label="ultimate product has Free Fiit workouts & plans worth £20 a month">
                            Free Fiit plans & workouts
                        </div>
                    ),
                    rowSpan: 1,
                },
                'Additional benefits': {
                    display: (
                        <Icon
                            name="plus"
                            color={palette.common.blue}
                            size={18}
                            accessibleTitle="additional benefits button expander"
                        />
                    ),
                    rowSpan: 1,
                },
                'Fitness progress tracking': {
                    display: (
                        <div aria-label="ultimate product has Fitness progress tracking">
                            Fitness progress tracking
                        </div>
                    ),
                    rowSpan: 1,
                },
            },
            title: monthlyProductTitleMap.UltimateMonthly,
            position: 0,
        },
        [TggMembershipType.StandardMonthly]: {
            includedBenefits: {
                ...getGymAccessAndFreeClassesCellContentV3(false, true),
                'Access all gyms': {
                    display: (
                        <span aria-label="standard product does not have Multi-gym access">
                            Single-gym access
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Free Freeze': {
                    display: (
                        <span aria-label="standard product does not have Freeze your monthly membership once for free">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
                ...getGuestPassBenefitV3(TggMembershipType.StandardMonthly),
                'Free Fiit plans & workouts': {
                    display: (
                        <span aria-label="standard product does not have Free Fiit workouts & plans worth £20 a month">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Additional benefits': {
                    display: (
                        <span aria-label="standard product does not have any additional benefits">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Fitness progress tracking': {
                    display: (
                        <span aria-label="standard product does not have Fitness progress tracking">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
            },
            title: monthlyProductTitleMap.StandardMonthly,
            position: 1,
        },
        [TggMembershipType.OffPeakMonthly]: {
            includedBenefits: {
                ...getGymAccessAndFreeClassesCellContentV3(true, false),
                'Access all gyms': {
                    display: (
                        <span aria-label="off-peak product does not have Multi-gym access">
                            Single-gym access
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Free Freeze': {
                    display: (
                        <span aria-label="off-peak product does not have Freeze your monthly membership once for free">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
                ...getGuestPassBenefitV3(TggMembershipType.OffPeakMonthly),
                'Free Fiit plans & workouts': {
                    display: (
                        <span aria-label="off-peak product does not have Free Fiit workouts & plans worth £20 a month">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Additional benefits': {
                    display: (
                        <span aria-label="off-peak product does not have any additional benefits">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
                'Fitness progress tracking': {
                    display: (
                        <span aria-label="off-peak product does not have Fitness & body composition tracking (where available)">
                            —
                        </span>
                    ),
                    rowSpan: 1,
                },
            },
            title: monthlyProductTitleMap.OffPeakMonthly,
            position: 2,
        },
        [TggMembershipType.StudentFitquestGymAccess]: studentDetails,
        [TggMembershipType.Student3]: studentDetails,
        [TggMembershipType.Student6]: studentDetails,
        [TggMembershipType.Student9]: studentDetails,
        [TggMembershipType.Student12]: studentDetails,
        [TggMembershipType.DayPass]: dayPassBenefitsMbtV3,
        [TggMembershipType.DayPass3]: dayPassBenefitsMbtV3,
        [TggMembershipType.DayPass5]: dayPassBenefitsMbtV3,
    } as unknown as PinoProductDetails;
};
